/**
 * iter225 — Money & number formatting utilities used across the ERP.
 *
 * Rules (locked by user):
 *  - Every rupee amount displays as `₹X,XXX.XX` (2 decimal places, comma
 *    thousands separator, Indian numbering system).
 *  - Every quantity displays with 2 decimals when non-integer, else integer.
 *  - Invoice totals get a Tally-style Round Off row (backend computes the
 *    round_off delta; the frontend just renders it when it's non-zero).
 *
 * Usage:
 *   import { fmtINR, fmtQty, fmtNumber } from '../utils/format';
 *   <span>{fmtINR(1234.5)}</span>            // → ₹1,234.50
 *   <span>{fmtINR(1234.5, { compact: true })}</span> // → ₹1,234
 *   <span>{fmtQty(50)}</span>                // → 50
 *   <span>{fmtQty(50.5, 'kg')}</span>        // → 50.50 kg
 */

const inr = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const inrCompact = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const numFmt = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * fmtINR(amount, { compact })
 *   compact=false (default) → "₹1,234.50"
 *   compact=true            → "₹1,234"
 */
export const fmtINR = (n, opts = {}) => {
  const v = Number(n) || 0;
  return (opts.compact ? inrCompact : inr).format(v);
};

/**
 * fmtNumber(value)  → "1,234.50"  (no currency prefix, 2 dp)
 */
export const fmtNumber = (n) => numFmt.format(Number(n) || 0);

/**
 * fmtQty(qty, unit)
 *   Integer qty stays integer, else 2 decimals. Unit appended if present.
 */
export const fmtQty = (n, unit = '') => {
  const v = Number(n) || 0;
  const isInt = Number.isInteger(v);
  const body = isInt ? new Intl.NumberFormat('en-IN').format(v) : numFmt.format(v);
  return unit ? `${body} ${unit}` : body;
};

/**
 * Round a rupee value to 2 decimals safely.
 */
export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Compute Tally-style round off delta for a bill total.
 *   applyRoundOff(1234.55) → { rounded: 1235, delta: 0.45 }
 * A POSITIVE delta means we ADDED paise to reach the round rupee, a
 * NEGATIVE delta means we DEDUCTED paise. The delta itself is capped at
 * ±₹0.99 per Tally convention.
 */
export const applyRoundOff = (total) => {
  const t = round2(total);
  const rounded = Math.round(t);
  const delta = round2(rounded - t);
  return { rounded, delta };
};
